import NextImage, { type ImageProps } from "next/image";

import { storyblokImage } from "@/lib/storyblok-image-loader";
import { cn } from "@/lib/utils";

function isLocal(src: ImageProps["src"]): src is string {
  return typeof src === "string" && src.startsWith("/");
}

function isSvg(src: ImageProps["src"]): boolean {
  return typeof src === "string" && /\.svg($|[?#])/i.test(src);
}

function ImagePlaceholder({
  fill,
  width,
  height,
  className,
  style,
  label,
}: {
  fill?: boolean;
  width?: ImageProps["width"];
  height?: ImageProps["height"];
  className?: string;
  style?: React.CSSProperties;
  label?: string;
}) {
  return (
    <span
      role="img"
      aria-label={label ? `Placeholder: ${label}` : "Image placeholder"}
      data-placeholder="image"
      className={cn(
        "bg-grey-01 flex items-center justify-center bg-[repeating-linear-gradient(135deg,transparent_0_14px,rgba(0,0,0,0.05)_14px_28px)] text-center",
        fill && "absolute inset-0 size-full",
        className,
      )}
      style={fill ? style : { width: width as number, height: height as number, ...style }}
    >
      <span className="text-caption text-grey-05 px-3 font-bold tracking-[0.08em] uppercase">
        {label ?? "Image to come"}
      </span>
    </span>
  );
}

export function focalPosition(focus: string | undefined | null, src: unknown): string | undefined {
  if (!focus || typeof src !== "string") return undefined;
  const point = focus.match(/^(\d+)x(\d+):/);
  const natural = src.match(/\/f\/\d+\/(\d+)x(\d+)\//);
  if (!point || !natural) return undefined;

  const x = (Number(point[1]) / Number(natural[1])) * 100;
  const y = (Number(point[2]) / Number(natural[2])) * 100;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
  return `${x.toFixed(2)}% ${y.toFixed(2)}%`;
}

function naturalSize(src: unknown): { width: number; height: number } | null {
  const natural = typeof src === "string" ? src.match(/\/f\/\d+\/(\d+)x(\d+)\//) : null;
  return natural ? { width: Number(natural[1]), height: Number(natural[2]) } : null;
}

function fallbackSrc(src: ImageProps["src"]): string | undefined {
  if (typeof src !== "string" || !src.includes("a.storyblok.com") || isSvg(src)) return undefined;
  return storyblokImage(src, 750);
}

function portraitBias(src: unknown, className: unknown): string | undefined {
  if (typeof src !== "string" || typeof className !== "string") return undefined;
  if (!className.includes("object-cover")) return undefined;
  if (/object-(top|bottom|left|right|center)\b|object-\[/.test(className)) return undefined;
  const natural = src.match(/\/f\/\d+\/(\d+)x(\d+)\//);
  if (!natural) return undefined;
  return Number(natural[2]) >= Number(natural[1]) ? "50% 25%" : undefined;
}

export function AssetImage({
  src,
  unoptimized,
  focus,
  style,
  ...rest
}: ImageProps & { focus?: string | null }) {
  const position =
    focalPosition(focus, src) ??
    ((style as React.CSSProperties | undefined)?.objectPosition
      ? undefined
      : portraitBias(src, (rest as ImageProps).className));
  const styleWithFocus = position ? { objectPosition: position, ...style } : style;
  if (!src) {
    const { alt, width, height, className, fill } = rest as ImageProps;
    return (
      <ImagePlaceholder
        fill={fill}
        width={width}
        height={height}
        className={className}
        style={styleWithFocus}
        label={alt || undefined}
      />
    );
  }

  if (isLocal(src) && isSvg(src)) {
    const { alt, width, height, className, priority, sizes, fill, ...img } = rest as ImageProps &
      Record<string, unknown>;
    void sizes;
    void fill;
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt={alt ?? ""}
        width={width as number | undefined}
        height={height as number | undefined}
        className={className}
        style={style}
        {...(priority
          ? { fetchPriority: "high" as const, loading: "eager" as const }
          : { loading: "lazy" as const, decoding: "async" as const })}
        {...(img as React.ImgHTMLAttributes<HTMLImageElement>)}
      />
    );
  }

  const { priority, fill, sizes, ...imageProps } = rest as Omit<ImageProps, "src">;
  const natural = fill ? naturalSize(src) : null;
  const loading = priority
    ? { preload: true, fetchPriority: "high" as const, loading: "eager" as const }
    : {};

  if (natural) {
    return (
      <NextImage
        src={src}
        unoptimized={unoptimized ?? isLocal(src)}
        overrideSrc={fallbackSrc(src)}
        width={natural.width}
        height={natural.height}
        sizes={sizes ?? "100vw"}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", ...styleWithFocus }}
        {...imageProps}
        {...loading}
      />
    );
  }

  return (
    <NextImage
      src={src}
      unoptimized={unoptimized ?? isLocal(src)}
      overrideSrc={fallbackSrc(src)}
      fill={fill}
      sizes={sizes}
      style={styleWithFocus}
      {...imageProps}
      {...loading}
    />
  );
}
